import Link from 'components/ui/AppLink';
import Loader from 'components/ui/LoadingIndicator';
import { Box } from '@mui/material';
import { MessageView } from 'components/ui/Feedback';
import React, { useEffect, useMemo } from 'react';

import { baseUrls } from '../../../utils/baseUrls.utils';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { GET_PREMIUM_CHANNEL_BY_ID_LEAN } from '../../../gql/channels';
import { stripePromise } from '../../../utils/stripe';
import { isValidMongoId } from '../../../utils/id-validation.utils';

const PremiumChannelSubscribeSuccessScreen = () => {
    const history = useHistory();
    const { channelId, status } = useParams<{
        channelId?: string;
        status?: string;
    }>();
    const isValidChannelId = isValidMongoId(channelId);
    const { search } = useLocation();
    const isSubscribed = status === 'subscribed';
    const isPayingInvoiceRequested = status === 'paying-invoice-requested';

    const { data, loading } = useQuery(GET_PREMIUM_CHANNEL_BY_ID_LEAN, {
        skip: !isValidChannelId,
        variables: { id: channelId },
        fetchPolicy: 'cache-first',
    });

    const channel = data?.channel;

    const paymentIntentSecret = useMemo(() => {
        const params = new URLSearchParams(search);
        return params.get('payment_intent_client_secret');
    }, [search]);

    useEffect(() => {
        if (!isValidChannelId) return;
        if (!paymentIntentSecret) return;

        stripePromise.then((stripe) => {
            stripe
                ?.retrievePaymentIntent(paymentIntentSecret)
                .then(({ paymentIntent }) => {
                    let message = '';
                    switch (paymentIntent?.status) {
                        case 'succeeded':
                            message =
                                "Congratulations! You're now subscribed and that is pretty cool.";
                            break;

                        case 'processing':
                            message =
                                "Payment processing. We'll update you when payment is received.";
                            break;

                        case 'requires_payment_method':
                            message =
                                'Payment failed. Please try another payment method.';
                            break;

                        default:
                            message = 'Something went wrong.';
                            break;
                    }
                    console.log(message);
                });
        });
    }, [paymentIntentSecret, isValidChannelId]);

    if (!isValidChannelId) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    height: 'calc(100vh - 212px)',
                    alignItems: 'center',
                }}
            >
                <MessageView
                    styleName="primary"
                    size="large"
                    title="Channel not found"
                    primaryAction={{
                        label: 'Return to Neon Insights',
                        onClick: () => history.push(baseUrls.forYou),
                        styleName: 'primary',
                    }}
                />
            </Box>
        );
    }

    if (loading || !data) return <Loader />;

    const generateHeaderText = () => {
        if (isPayingInvoiceRequested) {
            return 'Thank you for submitting your subscription request';
        } else {
            return `You have successfully ${isSubscribed ? 'subscribed to' : 'unsubscribed from'} ${channel?.name}`;
        }
    };

    const generateSubheaderText = () => {
        if (isPayingInvoiceRequested) {
            return 'The Neon Concierge Team will finalise your subscription shortly.';
        } else {
            return isSubscribed ? (
                <>
                    To see all your subscriptions,{' '}
                    <Link
                        to={baseUrls.subscriptions}
                        sx={{ fontSize: 'inherit' }}
                    >
                        manage your interests
                    </Link>
                </>
            ) : (
                <>
                    If you unsubscribed by accident, you are welcome to
                    resubscribe on the
                    <Link
                        to={baseUrls.channels.main}
                        sx={{ fontSize: 'inherit' }}
                    >
                        {' '}
                        Browse{' '}
                    </Link>{' '}
                    page
                </>
            );
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                height: 'calc(100vh - 212px)',
                alignItems: 'center',
            }}
        >
            <MessageView
                styleName="success"
                title={generateHeaderText()}
                primaryAction={{
                    label:
                        isSubscribed || isPayingInvoiceRequested
                            ? 'View Channel'
                            : 'Browse Insights',
                    onClick: () =>
                        history.push(
                            isSubscribed || isPayingInvoiceRequested
                                ? `${baseUrls.browse.channelMain}/${channelId}`
                                : baseUrls.channels.main
                        ),
                    styleName: 'success',
                }}
            >
                {generateSubheaderText()}
            </MessageView>
        </Box>
    );
};

export default PremiumChannelSubscribeSuccessScreen;
